import type { ModelConfigSource } from "@/api/client";

export type ModelConfigSourceLabel = ModelConfigSource | "binding_field_mapping";

export const MODEL_CONFIG_SOURCE_LABEL: Record<ModelConfigSourceLabel, string> = {
  binding: "绑定",
  site: "站点",
  request: "分发",
  template: "模板",
  default: "默认",
  binding_field_mapping: "绑定 field_mapping",
};

export function modelSourceLabel(source: ModelConfigSourceLabel | null | undefined): string {
  if (!source) return "默认";
  return MODEL_CONFIG_SOURCE_LABEL[source] ?? source;
}

export type ModelConfigLayer = {
  models: string | null | undefined;
  modelMapping: string | null | undefined;
};

export type ModelConfigLayers = {
  binding?: ModelConfigLayer;
  site?: ModelConfigLayer;
  distribution?: ModelConfigLayer;
  template?: ModelConfigLayer;
  pushFieldMapping?: string;
};

export type ResolvedModelConfig = {
  models: string;
  modelMapping: string;
  modelsSource: ModelConfigSourceLabel;
  modelMappingSource: ModelConfigSourceLabel;
};

function trimValue(value: string | null | undefined): string {
  return (value ?? "").trim();
}

function firstNonEmpty(
  layers: { value: string | null | undefined; source: ModelConfigSourceLabel }[],
): { value: string; source: ModelConfigSourceLabel } {
  for (const layer of layers) {
    const value = trimValue(layer.value);
    if (value !== "") return { value, source: layer.source };
  }
  return { value: "", source: "default" };
}

export function fieldMappingOverrides(fieldMapping: string | null | undefined): {
  models?: string;
  modelMapping?: string;
} {
  if (!fieldMapping || fieldMapping.trim() === "") return {};
  try {
    const entries = JSON.parse(fieldMapping) as { key?: string; value?: string }[];
    if (!Array.isArray(entries)) return {};
    const out: { models?: string; modelMapping?: string } = {};
    for (const entry of entries) {
      const key = entry?.key;
      const value = typeof entry?.value === "string" ? entry.value.trim() : "";
      if (value === "") continue;
      if (key === "models") out.models = value;
      if (key === "model_mapping") out.modelMapping = value;
    }
    return out;
  } catch {
    return {};
  }
}

export function resolveModelConfigLayers(layers: ModelConfigLayers): ResolvedModelConfig {
  const models = firstNonEmpty([
    { value: layers.binding?.models, source: "binding" },
    { value: layers.site?.models, source: "site" },
    { value: layers.distribution?.models, source: "request" },
    { value: layers.template?.models, source: "template" },
  ]);
  const mapping = firstNonEmpty([
    { value: layers.binding?.modelMapping, source: "binding" },
    { value: layers.site?.modelMapping, source: "site" },
    { value: layers.distribution?.modelMapping, source: "request" },
    { value: layers.template?.modelMapping, source: "template" },
  ]);

  const overrides = fieldMappingOverrides(layers.pushFieldMapping);
  return {
    models: overrides.models ?? models.value,
    modelMapping: overrides.modelMapping ?? mapping.value,
    modelsSource: overrides.models ? "binding_field_mapping" : models.source,
    modelMappingSource: overrides.modelMapping ? "binding_field_mapping" : mapping.source,
  };
}
